import React, { HtmlHTMLAttributes, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ProductType, SubscriptionPeriod } from '@meitu/subscribe-core';
import {
  getDisplayPrice,
  getPayChannels,
  getTeamSubscriptionPayPrice,
  isMobileH5,
  isWeb,
} from '@/utils/vip';
import { openBrowser } from '@/utils/native';
import { getUrlParam } from '@/utils';
import WeChartIcon from '@/assets/image/vip/weChat.png';
import AlipayIcon from '@/assets/image/vip/alipay.png';
import {
  PurchaseScene,
  SubscribeType,
  channelsMap,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { Checkbox, Tooltip } from 'antd';
import { PromotionType, TabType } from '@/constants/subscription';
import { VipInfo } from '@/services/user/index.d';
import { useAtom } from 'jotai';
import {
  auditVersionStatusAtom,
  curProductAtom,
  curTabAtom,
  hasCmbChinaToPaidAtom,
  hasFetchUserInfoAtom,
  loadingAtom,
  payUrlAtom,
  qrCodeUrlAtom,
  seatsAtom,
  userInfoAtom,
  vipInfoAtom,
} from '@/store';
import { track } from '@/utils/common';
import defaultQrCode from '@/assets/image/vip/default-qrcode.png';
import Modal from '../modal';

import styles from './index.module.css';
import { useLocaleStore } from '@/i18n/localeStore';

interface IProps extends HtmlHTMLAttributes<HTMLDivElement> {
  handleLogin?: () => void;
  onRefresh?: () => void;
  useMeidou?: boolean;
}

const ProductPay: React.FC<IProps> = (props: IProps) => {
  const { $t } = useLocaleStore();
  const { children, handleLogin, onRefresh, useMeidou } = props;
  const type = getUrlParam('type');
  const [checked, setChecked] = useState<boolean>(false);
  const [visibleAgreementConfirm, setVisibleAgreementConfirm] = useState<boolean>(false);

  const [loading] = useAtom(loadingAtom);
  const [curTab] = useAtom(curTabAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [seats] = useAtom(seatsAtom);
  const [hasFetchUserInfo] = useAtom(hasFetchUserInfoAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [curProduct] = useAtom(curProductAtom);
  const selectedAddOnProduct = curProduct?.addOn?.products?.find((item) => item.selected);
  const payProduct = selectedAddOnProduct || curProduct;
  const [auditVersionStatus] = useAtom(auditVersionStatusAtom);
  const [payUrl] = useAtom(payUrlAtom);
  const [qrCodeUrl] = useAtom(qrCodeUrlAtom);
  const [hasCmbChinaToPaid] = useAtom(hasCmbChinaToPaidAtom);

  // H5 须手动勾选协议后才能支付；桌面仍按审核版本 + 连续订阅商品控制
  const needAgreementCheckbox =
    isMobileH5 ||
    (auditVersionStatus && curProduct?.productType === ProductType.RenewedSubscription);

  const noLogin = hasFetchUserInfo && !userInfo?.id;

  const handleJumpTerm = () => {
    let params = {};

    if (isWeb) {
      params = {
        class: 'vip_tk',
        name_id: curProduct?.productId,
      };
    } else {
      params = {
        category: type === 'svip' ? '批处理条款' : '会员条款',
        product_id: curProduct?.productId,
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    let prefix = process.env.REACT_APP_ENV;

    if (process.env.REACT_APP_ENV === 'release') {
      prefix = '';
    }

    let url = isWeb
      ? 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html'
      : 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreement-web.html';

    if (useMeidou) {
      url = 'https://www.designkit.cn/meidou-service';
    } else if (subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT) {
      url = `https://pro.meitu.com/posterlabs/rules/web/koutu-service.html`;
    } else if (type === 'svip') {
      // 批处理
      url = 'https://pc.meitu.com/terms-svip';
    }

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  const handleJumpAuth = () => {
    const url = 'https://pro.meitu.com/posterlabs/rules/web/powerOfAttorney.html';

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  // 页面第一次加载商品并请求交易二维码的时候才需要展示骨架屏
  const showSkeleton = !curProduct && !(isMobileH5 ? payUrl : qrCodeUrl);

  const proceedPay = () => {
    if (loading) return;
    if (!payUrl) {
      onRefresh && onRefresh();
      return;
    }
    window.open(payUrl);
  };

  const handlePayClick = () => {
    if (noLogin && curProduct?.productId) {
      handleLogin && handleLogin();
      return;
    }
    if (isMobileH5 && needAgreementCheckbox && !checked) {
      setVisibleAgreementConfirm(true);
      return;
    }
    if (needAgreementCheckbox && !checked) return;
    proceedPay();
  };

  const handleConfirmAgreeAndPay = () => {
    setChecked(true);
    setVisibleAgreementConfirm(false);
    proceedPay();
  };

  const promotion = (curProduct?.promotions || [])[0] || {};
  // 是否免费试用商品, 2、4
  const isFreeTrial =
    promotion.promotionType === PromotionType.RecommendedFreeTrial ||
    promotion.promotionType === PromotionType.SalesFreeTrial;
  // 免费试用文案
  let freeTrialText = '';
  if (isFreeTrial) {
    const trialDuration = promotion.promotionDuration?.duration || '';
    switch (promotion?.promotionDuration?.period) {
      case 0:
        freeTrialText = $t((v, { r }) => r(v.Text_FreeTrialDays, [trialDuration]));
        break;
      case 1:
        freeTrialText = $t((v, { r }) => r(v.Text_FreeTrialMonths, [trialDuration]));
        break;
      case 2:
        freeTrialText = $t((v, { r }) => r(v.Text_FreeTrialQuarters, [trialDuration]));
        break;
      case 3:
        freeTrialText = $t((v, { r }) => r(v.Text_FreeTrialYears, [trialDuration]));
        break;
      default:
    }
  }

  const getNewEndDate = () => {
    const duration = curProduct?.subPeriodDuration || 0;
    if (curProduct?.subPeriod === SubscriptionPeriod.Day) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'day')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerMonth) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'month')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerQuarter) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration * 3, 'month')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerYear) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'year')
        .format('YYYY-MM-DD');
    }
  };

  // 支付渠道
  const payChannels: string[] = getPayChannels(payProduct) || [];

  const renderTipText = () => {
    // if (subscribeType === SubscribeType.CONSUMABLE) {
    //   // 单购
    //   const payChannelsText = payChannels
    //     ?.map((item: string) => {
    //       return channelsMap[item];
    //     })
    //     ?.join('、');

    //   return (
    //     <>
    //       {payChannels.includes('weixin') ? (
    //         <img className={styles.icon} src={WeChartIcon} alt="" />
    //       ) : null}
    //       {payChannels.includes('alipay') ? (
    //         <img className={styles.icon} src={AlipayIcon} alt="" />
    //       ) : null}
    //       <span>{`${payChannelsText}扫码${
    //         product?.productType === ProductType.Consumable ? '购买' : '开通'
    //       }`}</span>
    //     </>
    //   );
    // }

    const payChannelsText = payChannels
      ?.filter((item) => (payChannels.length > 1 ? item !== 'weixin' : true))
      ?.map((item: string) => channelsMap[item])
      ?.filter(Boolean)
      ?.join('、');

    return (
      <>
        {payChannels?.length === 1 && payChannels.includes('weixin') ? (
          <img className={styles.icon} src={WeChartIcon} alt="" />
        ) : null}
        {payChannels.includes('alipay') ? (
          <img className={styles.icon} src={AlipayIcon} alt="" />
        ) : null}
        <span>
          {$t((v, { r }) =>
            r(
              curProduct?.productType === ProductType.Consumable
                ? v.Text_ScanToBuy
                : v.Text_ScanToActivate,
              [payChannelsText]
            )
          )}
        </span>
        {payChannels?.length > 1 && payChannels?.includes('weixin') ? (
          <span className={styles.subTip}>
            {$t((v) =>
              curProduct?.productType === ProductType.Consumable
                ? v.Text_AlsoSupportWechatBuy
                : v.Text_AlsoSupportWechatActivate
            )}
          </span>
        ) : null}
      </>
    );
  };

  let termText = isWeb
    ? $t((v) => v.Text_ServiceAgreement_DesignKit)
    : $t((v) => v.Text_ServiceAgreement_MeituXiuXiu);
  if (useMeidou) {
    termText = $t((v) => v.Text_ServiceAgreement_Meidou);
  } else if (subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT) {
    termText = $t((v) => v.Text_ServiceAgreement_Cutout);
  } else if (type === 'svip') {
    // 批处理
    termText = $t((v) => v.Text_ServiceAgreement_BatchMeituXiuXiu);
  }

  let payPrice = getDisplayPrice(payProduct).payPrice;

  // 团队版下的支付价计算
  if (isTeamSubscription && curTab === TabType.SUB) {
    payPrice = getTeamSubscriptionPayPrice(curProduct, seats) ?? '';
  }

  const renderPayBtnText = () => {
    if (noLogin && curProduct?.productId) {
      return $t((v) => v.Button_ClickLogin);
    }
    if (loading) {
      return '';
    }
    if (!payUrl) {
      return $t((v) => v.Button_ClickRefresh);
    }
    if (isFreeTrial) {
      return freeTrialText;
    }
    return `${$t((v) => v.Label_Pay)}${getDisplayPrice(payProduct).moneySymbol || ''} ${
      payPrice || '-'
    }`;
  };

  const renderTerm = () => (
    <div className={classNames(styles.term, showSkeleton && styles.skeleton)}>
      {needAgreementCheckbox ? (
        <Tooltip
          open={!isMobileH5 && !checked}
          placement="bottom"
          title={$t((v) => v.Text_CheckAgreementFirst)}
        >
          <Checkbox
            className={classNames(
              styles.agreementCheckbox,
              curTab === TabType.PRO_SUB && styles.agreementCheckboxPro
            )}
            checked={checked}
            style={{ marginRight: 5 }}
            onChange={(e) => setChecked(e.target.checked)}
          ></Checkbox>
        </Tooltip>
      ) : null}
      <div>
        <>{needAgreementCheckbox ? $t((v) => v.Button_Agree) : $t((v) => v.PaymentAgreementHint)}</>
        <span className={styles.link} onClick={handleJumpTerm}>
          《{termText}》
        </span>
        {isTeamSubscription && curTab === TabType.SUB ? (
          <span className={styles.link} onClick={handleJumpAuth}>
            {$t((v) => v.CommercialLicenseAgreement)}
          </span>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(styles.module, {
        [styles.meidou]: SubscribeType.MEIDOU === subscribeType,
      })}
    >
      <div className={styles.body}>
        {!isMobileH5 ? (
          !showSkeleton && qrCodeUrl && (!needAgreementCheckbox || checked) ? (
            <img
              className={styles.qrcode}
              src={hasCmbChinaToPaid ? defaultQrCode : qrCodeUrl}
              alt=""
            />
          ) : (
            <div
              className={classNames(
                styles.qrcode,
                !(noLogin && curProduct?.productId) ? styles.skeleton : styles.noLoginQrcode
              )}
            >
              {noLogin && curProduct?.productId ? (
                <div className={styles.noLogin} onClick={() => handleLogin && handleLogin()}>
                  {$t((v) => v.Button_ClickLogin)}
                </div>
              ) : loading ? (
                ''
              ) : !checked && needAgreementCheckbox ? (
                <div className={styles.noLogin}>{$t((v) => v.Text_CheckAgreementFirst)}</div>
              ) : (
                <div className={styles.noLogin} onClick={() => onRefresh && onRefresh()}>
                  {$t((v) => v.Text_LoadError)}
                  <br />
                  {$t((v) => v.Button_ClickRefresh)}
                </div>
              )}
            </div>
          )
        ) : null}

        <div className={styles.info}>
          {!isMobileH5 ? (
            <div className={classNames(styles.payPrice, showSkeleton && styles.skeleton)}>
              {!isFreeTrial ? (
                <>
                  {$t((v) => v.Label_Pay)}
                  <span className={styles.symbol}>{getDisplayPrice(payProduct).moneySymbol}</span>
                  <span className={styles.price}>{payPrice}</span>
                </>
              ) : (
                <span className={styles.freeTrialText}>{freeTrialText}</span>
              )}
            </div>
          ) : null}
          {/* 团队版续费需要展示 */}
          {isTeamSubscription &&
          curTab === TabType.SUB &&
          purchaseScene === PurchaseScene.NORMAL &&
          vipInfo?.is_vip ? (
            <div className={classNames(styles.expireDate, showSkeleton && styles.skeleton)}>
              {$t((v, { r }) => r(v.Text_ExtendAfterPurchase, [getNewEndDate()]))}
            </div>
          ) : null}

          {!isMobileH5 ? (
            <div className={classNames(styles.tip, showSkeleton && styles.skeleton)}>
              {renderTipText()}
            </div>
          ) : null}
          {!isMobileH5 ? renderTerm() : null}
          {!isMobileH5 ? children : null}
        </div>
        {isMobileH5 ? (
          <>
            <button
              type="button"
              className={classNames(
                styles.payBtn,
                curTab === TabType.PRO_SUB && styles.payBtnPro,
                showSkeleton && styles.skeleton
              )}
              onClick={handlePayClick}
            >
              {renderPayBtnText()}
            </button>
            {renderTerm()}
            <Modal
              visible={visibleAgreementConfirm}
              hideClose
              style={{
                backgroundColor: '#181a1c',
                width: 'auto',
                height: 'auto',
              }}
            >
              <div className={styles.agreementConfirm}>
                <div className={styles.agreementConfirmTitle}>
                  {$t((v) => v.Text_AgreementConfirmTitle)}
                  <span className={styles.link} onClick={handleJumpTerm}>
                    《{termText}》
                  </span>
                </div>
                <div className={styles.agreementConfirmBtns}>
                  <button
                    type="button"
                    className={styles.agreementConfirmBtnSecondary}
                    onClick={() => setVisibleAgreementConfirm(false)}
                  >
                    {$t((v) => v.Button_ThinkAgain)}
                  </button>
                  <button
                    type="button"
                    className={classNames(
                      styles.agreementConfirmBtnPrimary,
                      curTab === TabType.PRO_SUB && styles.payBtnPro
                    )}
                    onClick={handleConfirmAgreeAndPay}
                  >
                    {$t((v) => v.Button_AgreeAndPay)}
                  </button>
                </div>
              </div>
            </Modal>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(ProductPay);
